import app from '@/app.js';
import { createVuetify } from 'vuetify';
import { aliases, md } from 'vuetify/iconsets/md';
import colors from 'vuetify/util/colors';
import { readPersistedStore } from '@/store/persistentStore';

// common config for all input components
const inputConfig = {
  attach: true,
  color: 'primary',
  clearIcon: 'clear',
  hideDetails: false,
  variant: 'outlined',
  density: 'compact',
  rounded: 'lg'
};

// this is used to set the default props for all components globally
const defaults = {
  VTextField: {
    ...inputConfig,
  },
  VAutocomplete: {
    ...inputConfig,
    autocomplete: 'off',
  },
  VTextarea: {
    ...inputConfig,
  },
  VSelect: {
    ...inputConfig,
  },
  VCard: {
    elevation: 0,
  },
  VBtn: {
    elevation: 0,
    large: true,
    dense: true,
    rounded: true,
    disabledColor: 'disabled',
  },
  VCheckboxBtn: {
    color: 'primary',
  },
  VAlert: {
    outlined: true,
    elevation: 0,
  },
  VListItem: {
    color: 'primary',
  },
  VCheckbox: {
    color: 'primary',
    density: 'compact',
  },
  VSwitch: {
    color: 'primary',
  },
  VRadio: {
    color: 'primary',
  },
  VLink: {
    color: 'primary',
  },
}

// Read the persisted theme before Vuetify boots so the first paint is already in
// the right mode.
const readPersistedTheme = () => readPersistedStore().theme?.mode;

const defaultTheme = readPersistedTheme() === 'dark' ? 'dark' : 'light';

const vuetify = createVuetify({
  icons: {
    defaultSet: 'md',
    aliases,
    sets: {
      md,
    },
  },
  // set the default component prop values
  defaults,
  theme: {
    defaultTheme,
    themes: {
      light: {
        dark: false,
        colors: {
          primary: colors.orange.darken2,
          secondary: colors.grey.darken3,
          accent: colors.orange.darken2,
          error: colors.red.darken1,
          warning: colors.yellow.darken2,
          info: colors.blue.darken1,
          highlight: colors.blue.darken1,
          success: colors.green.darken1,
          disabled: colors.grey.lighten1,
        },
      },
      dark: {
        dark: true,
        colors: {
          primary: colors.orange.lighten1,
          secondary: colors.grey.darken2,
          accent: colors.orange.lighten1,
          error: colors.red.lighten1,
          warning: colors.yellow.darken1,
          info: colors.blue.lighten1,
          highlight: colors.blue.lighten1,
          success: colors.green.lighten1,
          disabled: colors.grey.darken1,
          // The dark palette lightens every accent, so white text on top of them
          // fails contrast badly (white on orange.lighten1 is 1.94:1, on
          // yellow.darken1 only 1.4:1). Black reads on all of them; secondary is
          // the one dark tone left, so it keeps white.
          'on-primary': colors.shades.black,
          'on-secondary': colors.shades.white,
          'on-accent': colors.shades.black,
          'on-error': colors.shades.black,
          'on-warning': colors.shades.black,
          'on-info': colors.shades.black,
          'on-highlight': colors.shades.black,
          'on-success': colors.shades.black,
        },
      },
    },
  },
  breakpoint: {
    thresholds: {
      xs: 420,
    },
    scrollbarWidth: 10,
  },
});

app.use(vuetify);

export default vuetify;

export { inputConfig, defaults };
